import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import I18nText from "../components/I18nText";
import LagunaDatePicker from "../components/LagunaDatePicker";
import PortalChrome from "../components/portal/PortalChrome";
import { track } from "../lib/analytics";
import { CALENDLY_SUCCESS_TEAM_URL } from "../config/constants";
import { employerMe } from "../lib/api";
import { formatPhone, isValidEmail, titleCaseName } from "../lib/dataHygiene";
import {
    abandonApplication,
    advanceApplication,
    backApplication,
    getApplication,
    getRiders,
    patchApplicationStep,
    startApplication,
    submitApplication,
    useAutosave,
} from "../lib/teamApply";
import { US_STATES } from "../lib/us_states";
import "./Admin.css";
import "./Team.css";
import "./TeamApplyWizard.css";

const TOTAL_STEPS = 12;

/* -------------------------------------------------------------------- */
/* Chrome — progress, save indicator, footer                             */
/* -------------------------------------------------------------------- */
function ProgressBar({ step }) {
    const { t } = useTranslation();
    const dots = [];
    for (let i = 1; i <= TOTAL_STEPS; i++) {
        dots.push(
            <span
                key={i}
                className={`wiz-dot${i === step ? " active" : i < step ? " done" : ""}`}
                data-testid={`wiz-dot-${i}`}
            />
        );
    }
    return (
        <div className="wiz-progress" data-testid="wiz-progress">
            <div className="wiz-progress-dots">{dots}</div>
            <div className="wiz-progress-text mono">
                {t("team.apply.wizard.stepOf", { n: step, total: TOTAL_STEPS })}
            </div>
        </div>
    );
}

function SaveIndicator({ status, savedAt }) {
    const { t } = useTranslation();
    const label =
        status === "saving"
            ? t("team.apply.wizard.saving")
            : status === "error"
              ? t("team.apply.wizard.saveError")
              : status === "saved"
                ? t("team.apply.wizard.savedJust")
                : t("team.apply.wizard.saveIdle");
    const cls =
        status === "saving"
            ? "saving"
            : status === "error"
              ? "error"
              : status === "saved"
                ? "saved"
                : "idle";
    return (
        <div className={`wiz-save ${cls}`} data-testid="wiz-save-indicator">
            <span className="wiz-save-dot" />
            <span className="mono">{label}</span>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Inline explainer                                                      */
/* -------------------------------------------------------------------- */
function Explainer({ i18nKey, values }) {
    const { t } = useTranslation();
    return (
        <div className="wiz-explainer" data-testid="wiz-explainer">
            <div className="wiz-explainer-label mono">{t("team.apply.wizard.explainerLabel")}</div>
            <p><I18nText i18nKey={i18nKey} values={values} /></p>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 1 — Welcome                                                    */
/* -------------------------------------------------------------------- */
function Screen1({ app, employer }) {
    const { t } = useTranslation();
    return (
        <div className="wiz-screen" data-testid="wiz-screen-1">
            <div className="section-label mono">{t("team.apply.s1.label")}</div>
            <h2>
                <I18nText
                    i18nKey="team.apply.s1.h2"
                    values={{ companyName: employer?.employer_name || "your company" }}
                />
            </h2>
            <p className="wiz-sub">{t("team.apply.s1.sub")}</p>
            <ul className="wiz-list">
                {[1, 2, 3].map((n) => (
                    <li key={n}>
                        <span className="wiz-bullet mono">0{n}</span>
                        {t(`team.apply.s1.bullet${n}`)}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 2 — Confirm company info                                       */
/* -------------------------------------------------------------------- */
const PAYROLL_FREQS = ["weekly", "biweekly", "semimonthly", "monthly"];

function defaultEffectiveDate() {
    // 1st of the month, ~60 days out — used by the date picker default view.
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + 2);
    return d.toISOString().slice(0, 10);
}

function Screen2({ app, setField }) {
    const { t } = useTranslation();
    const cci = app.confirmed_company_info || {};
    const up = (k) => (e) => setField(`confirmed_company_info.${k}`, e.target.value);

    return (
        <div className="wiz-screen" data-testid="wiz-screen-2">
            <div className="section-label mono">{t("team.apply.s2.label")}</div>
            <h2><I18nText i18nKey="team.apply.s2.h2" /></h2>
            <p className="wiz-sub">{t("team.apply.s2.sub")}</p>

            <div className="wiz-grid-2">
                <Field label={t("team.apply.s2.legalName")} testId="s2-legal-name">
                    <input type="text" value={cci.legal_name || ""} onChange={up("legal_name")} />
                </Field>
                <Field label={t("team.apply.s2.dbaName")} testId="s2-dba-name">
                    <input type="text" value={cci.dba_name || ""} onChange={up("dba_name")} />
                </Field>
                <Field label={t("team.apply.s2.address1")} testId="s2-address1" wide>
                    <input type="text" value={cci.address1 || ""} onChange={up("address1")} />
                </Field>
                <Field label={t("team.apply.s2.address2")} testId="s2-address2" wide>
                    <input type="text" value={cci.address2 || ""} onChange={up("address2")} />
                </Field>
                <Field label={t("team.apply.s2.city")} testId="s2-city">
                    <input type="text" value={cci.city || ""} onChange={up("city")} />
                </Field>
                <Field label={t("team.apply.s2.state")} testId="s2-state">
                    <select
                        className="wiz-select"
                        value={cci.state || ""}
                        onChange={up("state")}
                    >
                        <option value="">{t("team.apply.wizard.selectPlaceholder")}</option>
                        {US_STATES.map((s) => (
                            <option key={s.value} value={s.value}>{s.name}</option>
                        ))}
                    </select>
                </Field>
                <Field label={t("team.apply.s2.postalCode")} testId="s2-zip">
                    <input type="text" value={cci.postal_code || ""} onChange={up("postal_code")} />
                </Field>
                <Field label={t("team.apply.s2.ein")} testId="s2-ein">
                    <input
                        type="text"
                        placeholder={cci.ein_set ? cci.ein_mask : "12-3456789"}
                        onChange={up("ein")}
                    />
                </Field>
                <Field label={t("team.apply.s2.headcount")} testId="s2-headcount">
                    <input
                        type="number"
                        min={10}
                        value={cci.headcount ?? ""}
                        onChange={(e) => setField("confirmed_company_info.headcount", e.target.value ? Number(e.target.value) : "")}
                    />
                </Field>
            </div>

            <Explainer i18nKey="team.apply.s2.explainerEin" />
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 3 — Coverage start + deduction schedule                        */
/* -------------------------------------------------------------------- */
function Screen3({ app, setField }) {
    const { t, i18n } = useTranslation();
    const defaultFreq = app.confirmed_company_info?.payroll_frequency;
    return (
        <div className="wiz-screen" data-testid="wiz-screen-3">
            <div className="section-label mono">{t("team.apply.s3.label")}</div>
            <h2><I18nText i18nKey="team.apply.s3.h2" /></h2>
            <p className="wiz-sub">{t("team.apply.s3.sub")}</p>

            {/* F6: effective date moved from Screen 2 */}
            <Field
                label={t("team.apply.s3.effectiveDate")}
                testId="s3-effective"
            >
                <LagunaDatePicker
                    value={app.effective_date_requested || ""}
                    onChange={(v) => setField("effective_date_requested", v)}
                    minDaysAhead={14}
                    defaultDate={defaultEffectiveDate()}
                    locale={i18n.resolvedLanguage || "en"}
                    placeholder={t("team.apply.s3.effectivePlaceholder")}
                    testid="s3-effective-picker"
                />
            </Field>

            <div className="wiz-field-section-label mono" style={{ marginTop: "1.75rem" }}>
                {t("team.apply.s3.deductionHeader")}
            </div>
            <div className="wiz-radio-group" data-testid="s3-options">
                {PAYROLL_FREQS.map((f) => {
                    const checked = app.deduction_frequency === f;
                    return (
                        <label
                            key={f}
                            className={`wiz-radio${checked ? " is-checked" : ""}`}
                            data-testid={`s3-opt-${f}`}
                        >
                            <input
                                type="radio"
                                name="deduction_frequency"
                                checked={checked}
                                onChange={() => setField("deduction_frequency", f)}
                            />
                            <span className="wiz-radio-label">
                                {t(`team.apply.wizard.payrollFreq.${f}`)}
                            </span>
                            {f === defaultFreq && (
                                <span className="wiz-radio-badge mono">
                                    {t("team.apply.s3.matchesPayroll")}
                                </span>
                            )}
                        </label>
                    );
                })}
            </div>
            <Explainer i18nKey="team.apply.s3.explainer" />
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 4 — Locations                                                  */
/* -------------------------------------------------------------------- */
function Screen4({ app, setField }) {
    const { t } = useTranslation();
    const lm = app.location_model;
    const locations = app.locations || [];
    const headcount = app.confirmed_company_info?.headcount || 0;
    const sum = locations.reduce((s, l) => s + (Number(l.employee_count) || 0), 0);
    const matched = lm === "multiple" && locations.length > 0 && sum === headcount;
    const mismatch = lm === "multiple" && locations.length > 0 && sum !== headcount;

    const updateLoc = (i, key, val) => {
        const next = [...locations];
        next[i] = { ...next[i], [key]: key === "employee_count" ? Number(val) || 0 : val };
        setField("locations", next);
    };
    const addLoc = () => {
        setField("locations", [...locations, { state: "", city: "", employee_count: 0 }]);
    };
    const removeLoc = (i) => {
        const next = locations.filter((_, idx) => idx !== i);
        setField("locations", next);
    };

    return (
        <div className="wiz-screen" data-testid="wiz-screen-4">
            <div className="section-label mono">{t("team.apply.s4.label")}</div>
            <h2><I18nText i18nKey="team.apply.s4.h2" /></h2>
            <div className="wiz-radio-group">
                {["single", "multiple"].map((opt) => {
                    const checked = lm === opt;
                    return (
                        <label key={opt} className={`wiz-radio${checked ? " is-checked" : ""}`}
                               data-testid={`s4-${opt}`}>
                            <input type="radio" name="location_model" checked={checked}
                                   onChange={() => setField("location_model", opt)} />
                            <span className="wiz-radio-label">{t(`team.apply.s4.opt.${opt}`)}</span>
                        </label>
                    );
                })}
            </div>

            {lm === "multiple" && (
                <div className="wiz-subgroup" data-testid="s4-locations">
                    {/* F7: running total pill — coral when mismatched, ink green when matched. */}
                    <div
                        className={`wiz-tally${matched ? " is-matched" : ""}${mismatch ? " is-mismatch" : ""}`}
                        data-testid="s4-tally"
                    >
                        <span className="wiz-tally-label mono">
                            {t("team.apply.s4.accountedFor")}
                        </span>
                        <span className="wiz-tally-value">
                            {sum} / {headcount}
                        </span>
                    </div>

                    {locations.map((loc, i) => (
                        <div className="wiz-row" key={i}>
                            <Field label={t("team.apply.s4.state")}>
                                <select
                                    className="wiz-select"
                                    value={loc.state || ""}
                                    onChange={(e) => updateLoc(i, "state", e.target.value)}
                                >
                                    <option value="">{t("team.apply.wizard.selectPlaceholder")}</option>
                                    {US_STATES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.name}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label={t("team.apply.s4.city")}>
                                <input type="text" value={loc.city || ""}
                                       onChange={(e) => updateLoc(i, "city", e.target.value)} />
                            </Field>
                            <Field label={t("team.apply.s4.count")}>
                                <input type="number" min={1} value={loc.employee_count || ""}
                                       onChange={(e) => updateLoc(i, "employee_count", e.target.value)} />
                            </Field>
                            <button
                                type="button"
                                className="wiz-row-remove"
                                onClick={() => removeLoc(i)}
                                aria-label={t("team.apply.s4.remove")}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                    <button type="button" className="btn-add-more" onClick={addLoc} data-testid="s4-add-location">
                        {t("team.apply.s4.addLocation")}
                    </button>
                    {mismatch && (
                        <div className="wiz-warn" data-testid="s4-mismatch">
                            {t("team.apply.s4.mismatch", { sum, headcount })}
                        </div>
                    )}
                </div>
            )}
            <Explainer i18nKey="team.apply.s4.explainer" />
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 5 — Class structure                                            */
/* -------------------------------------------------------------------- */
function Screen5({ app, setField }) {
    const { t } = useTranslation();
    const cs = app.class_structure;
    const classes = app.classes || [];
    const update = (i, key, val) => {
        const next = [...classes];
        next[i] = { ...next[i], [key]: key === "eligibility_wait_days" || key === "minimum_hours_per_week" ? Number(val) || 0 : (key === "eligible_for_coverage" ? val : val) };
        setField("classes", next);
    };
    const addClass = () => {
        setField("classes", [
            ...classes,
            { name: "", eligibility_wait_days: 30, minimum_hours_per_week: 30, eligible_for_coverage: true },
        ]);
    };
    const removeClass = (i) => setField("classes", classes.filter((_, idx) => idx !== i));

    return (
        <div className="wiz-screen" data-testid="wiz-screen-5">
            <div className="section-label mono">{t("team.apply.s5.label")}</div>
            <h2><I18nText i18nKey="team.apply.s5.h2" /></h2>
            <div className="wiz-radio-group">
                {["single_class", "multiple_classes"].map((opt) => {
                    const checked = cs === opt;
                    return (
                        <label key={opt} className={`wiz-radio${checked ? " is-checked" : ""}`}
                               data-testid={`s5-${opt}`}>
                            <input type="radio" name="class_structure" checked={checked}
                                   onChange={() => setField("class_structure", opt)} />
                            <span className="wiz-radio-label">{t(`team.apply.s5.opt.${opt}`)}</span>
                        </label>
                    );
                })}
            </div>
            {cs === "multiple_classes" && (
                <div className="wiz-subgroup" data-testid="s5-classes">
                    {classes.map((c, i) => (
                        <div className="wiz-row" key={i}>
                            <Field label={t("team.apply.s5.className")}>
                                <input type="text" value={c.name || ""}
                                       onChange={(e) => update(i, "name", e.target.value)} />
                            </Field>
                            <Field label={t("team.apply.s5.waitDays")}>
                                <input type="number" min={0} value={c.eligibility_wait_days}
                                       onChange={(e) => update(i, "eligibility_wait_days", e.target.value)} />
                            </Field>
                            <Field label={t("team.apply.s5.minHours")}>
                                <input type="number" min={0} value={c.minimum_hours_per_week}
                                       onChange={(e) => update(i, "minimum_hours_per_week", e.target.value)} />
                            </Field>
                            <Field label={t("team.apply.s5.eligible")}>
                                <input type="checkbox" checked={c.eligible_for_coverage !== false}
                                       onChange={(e) => update(i, "eligible_for_coverage", e.target.checked)} />
                            </Field>
                            <button type="button" className="link-inline" onClick={() => removeClass(i)}>✕</button>
                        </div>
                    ))}
                    <button type="button" className="btn-add-more" onClick={addClass} data-testid="s5-add-class">
                        {t("team.apply.s5.addClass")}
                    </button>
                </div>
            )}
            <Explainer i18nKey="team.apply.s5.explainer" />
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 6 — Eligibility window                                         */
/* -------------------------------------------------------------------- */
function Screen6({ app, setField }) {
    const { t } = useTranslation();
    const ew = app.eligibility_window;
    return (
        <div className="wiz-screen" data-testid="wiz-screen-6">
            <div className="section-label mono">{t("team.apply.s6.label")}</div>
            <h2><I18nText i18nKey="team.apply.s6.h2" /></h2>
            <div className="wiz-radio-group">
                {["year_round", "annual_open_enrollment"].map((opt) => {
                    const checked = ew === opt;
                    return (
                        <label key={opt} className={`wiz-radio${checked ? " is-checked" : ""}`}
                               data-testid={`s6-${opt}`}>
                            <input type="radio" name="eligibility_window" checked={checked}
                                   onChange={() => setField("eligibility_window", opt)} />
                            <span className="wiz-radio-label">{t(`team.apply.s6.opt.${opt}`)}</span>
                        </label>
                    );
                })}
            </div>
            {ew === "year_round" && (
                <div className="wiz-subgroup">
                    <Field label={t("team.apply.s6.waitLabel")} testId="s6-wait">
                        <input type="number" min={0} max={180}
                               value={app.waiting_period_days ?? 30}
                               onChange={(e) => setField("waiting_period_days", Number(e.target.value) || 0)} />
                    </Field>
                </div>
            )}
            <Explainer i18nKey="team.apply.s6.explainer" />
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 7 — Rider elections                                            */
/* -------------------------------------------------------------------- */
function Screen7({ app, setField }) {
    const { t } = useTranslation();
    const rd = app.rider_elections || {};
    const set = (k, v) => setField(`rider_elections.${k}`, v);
    return (
        <div className="wiz-screen" data-testid="wiz-screen-7">
            <div className="section-label mono">{t("team.apply.s7.label")}</div>
            <h2><I18nText i18nKey="team.apply.s7.h2" /></h2>
            <Explainer i18nKey="team.apply.s7.explainer" />

            <Toggle
                testId="s7-wp"
                title={t("team.apply.s7.wpTitle")}
                cost={t("team.apply.s7.wpCost")}
                description={t("team.apply.s7.wpDescription")}
                on={rd.waiver_of_premium !== false}
                onChange={(v) => set("waiver_of_premium", v)}
            />
            <Toggle
                testId="s7-adb"
                title={t("team.apply.s7.adbTitle")}
                cost={t("team.apply.s7.adbCost")}
                description={t("team.apply.s7.adbDescription")}
                on={!!rd.accidental_death_benefit}
                onChange={(v) => set("accidental_death_benefit", v)}
            />
            <Toggle
                testId="s7-dep"
                title={t("team.apply.s7.depTitle")}
                cost={t("team.apply.s7.depCost")}
                description={t("team.apply.s7.depDescription")}
                on={rd.dependent_coverage === "offered"}
                onChange={(v) => set("dependent_coverage", v ? "offered" : "not_offered")}
            />
        </div>
    );
}

function Toggle({ testId, title, cost, description, on, onChange }) {
    return (
        <div className={`wiz-toggle${on ? " is-on" : ""}`} data-testid={testId}>
            <div className="wiz-toggle-head">
                <div>
                    <div className="wiz-toggle-title">{title}</div>
                    <div className="wiz-toggle-cost mono">{cost}</div>
                </div>
                <button
                    type="button"
                    className={`wiz-pill${on ? " is-on" : ""}`}
                    role="switch"
                    aria-checked={on}
                    onClick={() => onChange(!on)}
                    data-testid={`${testId}-switch`}
                >
                    <span className="wiz-pill-knob" />
                </button>
            </div>
            <p className="wiz-toggle-desc">{description}</p>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 8 — ERISA self-determination                                   */
/* -------------------------------------------------------------------- */
function Term({ i18nKey, defKey }) {
    const { t } = useTranslation();
    return (
        <span className="wiz-term" title={t(defKey)}>
            {t(i18nKey)}
        </span>
    );
}

function Screen8({ app, setField }) {
    const { t } = useTranslation();
    const es = app.erisa_status;
    return (
        <div className="wiz-screen" data-testid="wiz-screen-8">
            <div className="section-label mono">{t("team.apply.s8.label")}</div>
            <h2><I18nText i18nKey="team.apply.s8.h2" /></h2>
            <p className="wiz-sub">
                <I18nText
                    i18nKey="team.apply.s8.explainer1"
                    components={{
                        t1: <Term i18nKey="team.apply.s8.termErisa" defKey="team.apply.s8.defErisa" />,
                        t2: <Term i18nKey="team.apply.s8.termSafeHarbor" defKey="team.apply.s8.defSafeHarbor" />,
                    }}
                />
            </p>
            <p className="wiz-sub">
                <I18nText
                    i18nKey="team.apply.s8.explainer2"
                    components={{
                        t1: <Term i18nKey="team.apply.s8.termForm5500" defKey="team.apply.s8.defForm5500" />,
                    }}
                />
            </p>
            <div className="wiz-radio-group wiz-radio-stack">
                {[
                    ["safe_harbor_exempt", "recommended"],
                    ["erisa_governed", "warning"],
                    ["pending_counsel_review", "pause"],
                ].map(([val, badge]) => {
                    const checked = es === val;
                    return (
                        <label
                            key={val}
                            className={`wiz-radio wiz-radio-block${checked ? " is-checked" : ""}`}
                            data-testid={`s8-${val}`}
                        >
                            <input type="radio" name="erisa_status" checked={checked}
                                   onChange={() => setField("erisa_status", val)} />
                            <div className="wiz-radio-body">
                                <div className="wiz-radio-title">
                                    {t(`team.apply.s8.opt.${val}.title`)}
                                    <span className={`wiz-badge wiz-badge-${badge} mono`}>
                                        {t(`team.apply.s8.opt.${val}.badge`)}
                                    </span>
                                </div>
                                <p>{t(`team.apply.s8.opt.${val}.desc`)}</p>
                            </div>
                        </label>
                    );
                })}
            </div>
            {es === "erisa_governed" && (
                <div className="wiz-warn" data-testid="s8-erisa-warning">
                    {t("team.apply.s8.erisaWarning")}
                </div>
            )}
            {es === "pending_counsel_review" && (
                <div className="wiz-note" data-testid="s8-counsel-note">
                    {t("team.apply.s8.counselNote")}
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 9 — HR contact                                                 */
/* -------------------------------------------------------------------- */
function Screen9({ app, setField, employer }) {
    const { t } = useTranslation();
    const hr = app.hr_admin_contact || {};
    const up = (k) => (e) => setField(`hr_admin_contact.${k}`, e.target.value);
    const blurName = (k) => (e) => setField(`hr_admin_contact.${k}`, titleCaseName(e.target.value));
    const blurEmail = (k) => (e) => setField(`hr_admin_contact.${k}`, e.target.value.trim());

    // Phone fields — decoupled shadow state.
    // Server stores digits-only (e.g. "5551234567"); UI shows formatted
    // "(555) 123-4567". formatPhone strips non-digits on input, so this
    // round-trips cleanly whether the stored record is digits-only (new)
    // or legacy-formatted (old). No data migration required.
    const hydrate = (raw) => {
        if (!raw) return "";
        const f = formatPhone(raw);
        return f.valid ? f.display : raw;
    };
    const [workDisplay, setWorkDisplay] = useState(() => hydrate(hr.work_phone));
    const [cellDisplay, setCellDisplay] = useState(() => hydrate(hr.cell_phone));

    // Re-hydrate when the server value changes from elsewhere (initial
    // fetch, another tab, wizard reset). Guard against clobbering the
    // user's in-progress typing: only resync when the server's
    // normalized-digits value differs from the local display's digits.
    useEffect(() => {
        const serverDigits = formatPhone(hr.work_phone || "").digits;
        const localDigits = formatPhone(workDisplay || "").digits;
        if (serverDigits !== localDigits) setWorkDisplay(hydrate(hr.work_phone));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hr.work_phone]);
    useEffect(() => {
        const serverDigits = formatPhone(hr.cell_phone || "").digits;
        const localDigits = formatPhone(cellDisplay || "").digits;
        if (serverDigits !== localDigits) setCellDisplay(hydrate(hr.cell_phone));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hr.cell_phone]);

    const commitPhone = (k, setter) => (e) => {
        const raw = e.target.value;
        const f = formatPhone(raw);
        // Display: formatted when valid, otherwise leave as-typed (trimmed).
        setter(f.valid ? f.display : raw.trim());
        // Server: digits-only when we have 10 digits, otherwise whatever
        // the user typed with non-digits stripped (so partial entries
        // still round-trip on resume without re-breaking the regex).
        const digitsOnly = f.digits || raw.replace(/\D+/g, "");
        setField(`hr_admin_contact.${k}`, digitsOnly);
    };

    const emailInvalid = !!hr.email && !isValidEmail(hr.email.trim());
    const workPhoneInvalid = !!workDisplay && !formatPhone(workDisplay).valid;
    const cellPhoneInvalid = !!cellDisplay && !formatPhone(cellDisplay).valid;
    // prefill from session on first mount if empty
    useEffect(() => {
        if (!hr.email && employer?.email) {
            setField("hr_admin_contact.email", employer.email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const hasPhone = !!(workDisplay || cellDisplay);
    return (
        <div className="wiz-screen" data-testid="wiz-screen-9">
            <div className="section-label mono">{t("team.apply.s9.label")}</div>
            <h2><I18nText i18nKey="team.apply.s9.h2" /></h2>
            <p className="wiz-sub">{t("team.apply.s9.sub")}</p>
            <div className="wiz-grid-2">
                <Field label={t("team.apply.s9.name")} testId="s9-name">
                    <input type="text" value={hr.name || ""} onChange={up("name")} onBlur={blurName("name")} />
                </Field>
                <Field label={t("team.apply.s9.title")} testId="s9-title">
                    <input type="text" value={hr.title || ""} onChange={up("title")} />
                </Field>
                <Field label={t("team.apply.s9.email")} testId="s9-email" wide>
                    <input data-testid="s9-email" type="email" value={hr.email || ""} onChange={up("email")} onBlur={blurEmail("email")} />
                    {emailInvalid && (
                        <div className="wiz-warn" data-testid="s9-email-err">{t("auth.common.emailInvalid")}</div>
                    )}
                </Field>
                <Field label={t("team.apply.s9.workPhone")} testId="s9-work-phone">
                    <input
                        data-testid="s9-work-phone"
                        type="tel"
                        value={workDisplay}
                        onChange={(e) => setWorkDisplay(e.target.value)}
                        onBlur={commitPhone("work_phone", setWorkDisplay)}
                        placeholder="(555) 123-4567"
                    />
                    {workPhoneInvalid && (
                        <div className="wiz-warn" data-testid="s9-work-phone-err">{t("auth.common.phoneInvalid")}</div>
                    )}
                </Field>
                <Field label={t("team.apply.s9.cellPhone")} testId="s9-cell-phone">
                    <input
                        data-testid="s9-cell-phone"
                        type="tel"
                        value={cellDisplay}
                        onChange={(e) => setCellDisplay(e.target.value)}
                        onBlur={commitPhone("cell_phone", setCellDisplay)}
                        placeholder={t("team.apply.s9.optional")}
                    />
                    {cellPhoneInvalid && (
                        <div className="wiz-warn" data-testid="s9-cell-phone-err">{t("auth.common.phoneInvalid")}</div>
                    )}
                </Field>
            </div>
            {!hasPhone && (
                <p className="wiz-note-inline mono" data-testid="s9-phone-required">
                    {t("team.apply.s9.phoneRequired")}
                </p>
            )}
            <Explainer i18nKey="team.apply.s9.explainer" />
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 10 — Communication preferences                                 */
/* -------------------------------------------------------------------- */
function Screen10({ app, setField }) {
    const { t } = useTranslation();
    const prefs = app.communication_preferences || {};
    const warn = prefs.physical_letters || prefs.voice_drops;
    // F9: labels keyed by compound name (email/physical_letters/voice_drops).
    const items = [
        ["email", t("team.apply.s10.emailLabel"), t("team.apply.s10.emailDesc")],
        ["physical_letters", t("team.apply.s10.physicalLabel"), t("team.apply.s10.physicalDesc")],
        ["voice_drops", t("team.apply.s10.voiceLabel"), t("team.apply.s10.voiceDesc")],
    ];
    return (
        <div className="wiz-screen" data-testid="wiz-screen-10">
            <div className="section-label mono">{t("team.apply.s10.label")}</div>
            <h2><I18nText i18nKey="team.apply.s10.h2" /></h2>
            <p className="wiz-sub">{t("team.apply.s10.sub")}</p>
            {items.map(([k, label, desc]) => (
                <label key={k} className="wiz-check" data-testid={`s10-${k}`}>
                    <input
                        type="checkbox"
                        checked={!!prefs[k]}
                        onChange={(e) => setField(`communication_preferences.${k}`, e.target.checked)}
                    />
                    <div>
                        <div className="wiz-check-title">{label}</div>
                        <p>{desc}</p>
                    </div>
                </label>
            ))}
            {warn && (
                <div className="wiz-warn" data-testid="s10-census-warn">
                    {t("team.apply.s10.censusWarn")}
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 11 — Review & Submit (Phase 1.5c)                              */
/* -------------------------------------------------------------------- */
const CALENDLY_URL = CALENDLY_SUCCESS_TEAM_URL;

function fmtFreq(x) {
    if (!x) return "—";
    const map = { weekly: "Weekly", biweekly: "Bi-weekly", semimonthly: "Semi-monthly", monthly: "Monthly" };
    return map[x] || x;
}
function fmtBool(v) { return v ? "Yes" : "No"; }

function ReviewBlock({ title, editStep, onEdit, children }) {
    return (
        <div className="review-block">
            <div className="review-block-head">
                <h3>{title}</h3>
                {editStep != null && (
                    <button
                        type="button"
                        className="review-block-edit"
                        onClick={() => onEdit(editStep)}
                        data-testid={`review-edit-${editStep}`}
                    >Edit step {editStep} ↗︎</button>
                )}
            </div>
            {children}
        </div>
    );
}

function Screen11({ app, onEdit, onSubmitted }) {
    const { t } = useTranslation();
    const [attested, setAttested] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);

    const cci = app.confirmed_company_info || {};
    const hr = app.hr_admin_contact || {};
    const comm = app.communication_preferences || {};
    const riders = app.rider_elections || {};
    const company = cci.legal_name || "—";

    const onSubmit = async () => {
        if (!attested || submitting) return;
        setSubmitting(true);
        setErr(null);
        track("team_apply_submit_click", { application_id: app.application_id });
        try {
            const res = await submitApplication(
                true,
                `I confirm the information above is accurate and authorize Laguna to submit this application to MassMutual on ${company}'s behalf.`,
            );
            track("team_apply_submit_success", { application_id: app.application_id });
            onSubmitted?.(res);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(
                typeof d === "object"
                    ? d?.message || "Please complete all prior steps."
                    : (d || "Submission failed. Please try again in a moment."),
            );
            track("team_apply_submit_fail", {});
        } finally {
            setSubmitting(false);
        }
    };

    const rstr = [];
    if (riders.waiver_of_premium) rstr.push("Waiver of Premium");
    if (riders.accidental_death_benefit) rstr.push("Accidental Death Benefit");
    if (riders.dependent_coverage && riders.dependent_coverage !== "none" && riders.dependent_coverage !== false) rstr.push("Dependent coverage");

    return (
        <div className="wiz-screen wiz-screen-11" data-testid="wiz-screen-11">
            <div className="section-label mono">{t("team.apply.review.label")}</div>
            <h2>
                {t("team.apply.review.h2a")} <em>{t("team.apply.review.h2b")}</em>
            </h2>
            <p className="wiz-sub">{t("team.apply.review.sub", { company })}</p>

            <div className="review-summary">
                <ReviewBlock title={t("team.apply.review.blocks.company")} editStep={2} onEdit={onEdit}>
                    <dl className="kv">
                        <dt>Legal name</dt><dd>{cci.legal_name || <span className="review-missing">Not set</span>}</dd>
                        <dt>DBA</dt><dd>{cci.dba_name || "—"}</dd>
                        <dt>Address</dt><dd>{[cci.address1, cci.address2, cci.city, cci.state, cci.postal_code].filter(Boolean).join(", ") || <span className="review-missing">Not set</span>}</dd>
                        <dt>EIN</dt><dd>{cci.ein_set ? (cci.ein_mask || "••-•••••••") : <span className="review-missing">Not provided</span>}</dd>
                        <dt>Headcount</dt><dd>{cci.headcount ?? <span className="review-missing">Not set</span>}</dd>
                    </dl>
                </ReviewBlock>

                <ReviewBlock title={t("team.apply.review.blocks.payroll")} editStep={3} onEdit={onEdit}>
                    <dl className="kv">
                        <dt>Payroll frequency</dt><dd>{fmtFreq(cci.payroll_frequency)}</dd>
                        <dt>Deduction frequency</dt><dd>{fmtFreq(app.deduction_frequency)}</dd>
                        <dt>Effective date</dt><dd>{app.effective_date_requested || <span className="review-missing">Not set</span>}</dd>
                    </dl>
                </ReviewBlock>

                <ReviewBlock title={t("team.apply.review.blocks.locations")} editStep={4} onEdit={onEdit}>
                    <dl className="kv">
                        <dt>Model</dt><dd>{app.location_model === "single" ? "Single location" : `${(app.locations || []).length} locations`}</dd>
                        {(app.locations || []).length > 0 && (
                            <>
                                <dt>Per-location headcount</dt>
                                <dd>{(app.locations || []).map((l, i) => `${l.name || `Location ${i+1}`}: ${l.employee_count || 0}`).join(", ")}</dd>
                            </>
                        )}
                    </dl>
                </ReviewBlock>

                <ReviewBlock title={t("team.apply.review.blocks.classes")} editStep={5} onEdit={onEdit}>
                    <dl className="kv">
                        <dt>Structure</dt><dd>{app.class_structure === "single_class" ? "Single class" : "Multiple classes"}</dd>
                        {app.class_structure === "multiple_classes" && (
                            <>
                                <dt>Classes</dt>
                                <dd>{(app.classes || []).map((c) => c.name).join(", ") || <span className="review-missing">None defined</span>}</dd>
                            </>
                        )}
                    </dl>
                </ReviewBlock>

                <ReviewBlock title={t("team.apply.review.blocks.eligibility")} editStep={6} onEdit={onEdit}>
                    <dl className="kv">
                        <dt>Window</dt><dd>{app.eligibility_window === "year_round" ? "Year-round rolling enrollment" : "Annual open enrollment"}</dd>
                        {app.eligibility_window === "year_round" && (
                            <>
                                <dt>Waiting period</dt>
                                <dd>{app.waiting_period_days != null ? `${app.waiting_period_days} days` : <span className="review-missing">Not set</span>}</dd>
                            </>
                        )}
                    </dl>
                </ReviewBlock>

                <ReviewBlock title={t("team.apply.review.blocks.riders")} editStep={7} onEdit={onEdit}>
                    <dl className="kv">
                        <dt>Riders elected</dt><dd>{rstr.join(", ") || "Core only"}</dd>
                    </dl>
                </ReviewBlock>

                <ReviewBlock title={t("team.apply.review.blocks.erisa")} editStep={8} onEdit={onEdit}>
                    <dl className="kv">
                        <dt>Status</dt><dd>{app.erisa_status || <span className="review-missing">Not set</span>}</dd>
                        {app.erisa_notes && (<><dt>Notes</dt><dd>{app.erisa_notes}</dd></>)}
                    </dl>
                </ReviewBlock>

                <ReviewBlock title={t("team.apply.review.blocks.hrContact")} editStep={9} onEdit={onEdit}>
                    <dl className="kv">
                        <dt>Name</dt><dd>{hr.name || <span className="review-missing">Not set</span>}</dd>
                        <dt>Email</dt><dd>{hr.email || <span className="review-missing">Not set</span>}</dd>
                        <dt>Phone</dt><dd>{hr.work_phone || hr.cell_phone || <span className="review-missing">Not set</span>}</dd>
                    </dl>
                </ReviewBlock>

                <ReviewBlock title={t("team.apply.review.blocks.comms")} editStep={10} onEdit={onEdit}>
                    <dl className="kv">
                        <dt>Email</dt><dd>{fmtBool(comm.email)}</dd>
                        <dt>Physical letters</dt><dd>{fmtBool(comm.physical_letters)}</dd>
                        <dt>Voice drops</dt><dd>{fmtBool(comm.voice_drops)}</dd>
                    </dl>
                </ReviewBlock>
            </div>

            <div className="attestation-wrap">
                <input
                    type="checkbox"
                    id="attest"
                    checked={attested}
                    onChange={(e) => setAttested(e.target.checked)}
                    data-testid="attest-checkbox"
                />
                <label htmlFor="attest">
                    <I18nText i18nKey="team.apply.review.attestation" values={{ company }} />
                </label>
            </div>

            {err && <div className="review-err" data-testid="review-err">{err}</div>}

            <div className="review-cta-row">
                <button
                    type="button"
                    className="admin-btn primary"
                    onClick={onSubmit}
                    disabled={!attested || submitting}
                    data-testid="wiz-submit-application"
                >
                    {submitting
                        ? t("team.apply.review.submitting")
                        : t("team.apply.review.submitCta")}
                </button>
                <a
                    className="admin-btn secondary"
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track("team_apply_talk_to_success", {})}
                    data-testid="wiz-talk-to-success"
                >
                    {t("team.apply.review.talkToSuccess")}
                </a>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Screen 12 — You're submitted                                          */
/* -------------------------------------------------------------------- */
function Screen12({ app, proposalId }) {
    const { t } = useTranslation();
    useEffect(() => { track("team_apply_screen_12_view", { application_id: app.application_id }); }, [app.application_id]);
    const company = (app.confirmed_company_info || {}).legal_name || "your team";
    return (
        <div className="wiz-screen" data-testid="wiz-screen-12">
            <div className="submitted-screen">
                <div className="section-label mono">{t("team.apply.submitted.label")}</div>
                <h2>
                    {t("team.apply.submitted.h2a")} <em>{t("team.apply.submitted.h2b")}</em>
                </h2>
                <p className="submitted-body">
                    {t("team.apply.submitted.body", { company })}
                </p>
                <ul className="timeline-steps" data-testid="submitted-timeline">
                    <li>
                        <div className="tl-when">{t("team.apply.submitted.tl.today")}</div>
                        <div className="tl-what">
                            {t("team.apply.submitted.tl.t1")}
                            <span className="sub">{t("team.apply.submitted.tl.s1")}</span>
                        </div>
                    </li>
                    <li>
                        <div className="tl-when">{t("team.apply.submitted.tl.days23")}</div>
                        <div className="tl-what">
                            {t("team.apply.submitted.tl.t2")}
                            <span className="sub">{t("team.apply.submitted.tl.s2")}</span>
                        </div>
                    </li>
                    <li>
                        <div className="tl-when">{t("team.apply.submitted.tl.days57")}</div>
                        <div className="tl-what">
                            {t("team.apply.submitted.tl.t3")}
                            <span className="sub">{t("team.apply.submitted.tl.s3")}</span>
                        </div>
                    </li>
                    <li>
                        <div className="tl-when">{t("team.apply.submitted.tl.day7")}</div>
                        <div className="tl-what">
                            {t("team.apply.submitted.tl.t4")}
                            <span className="sub">{t("team.apply.submitted.tl.s4")}</span>
                        </div>
                    </li>
                </ul>
                <p className="submitted-body" style={{ marginBottom: "2rem" }}>
                    {t("team.apply.submitted.emailNote")}
                </p>
                {proposalId && (
                    <a
                        className="back-to-portal"
                        href={`/portal/proposal/${proposalId}`}
                        data-testid="submitted-back-to-portal"
                    >
                        ←︎ {t("team.apply.submitted.backToPortal")}
                    </a>
                )}
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Field helper                                                          */
/* -------------------------------------------------------------------- */
function Field({ label, testId, wide, children }) {
    // Place the testid on the first interactive child (input/select/textarea)
    // so Playwright page.fill / page.select_option target it directly. The
    // wrapper also gets a scoped `-wrap` testid in case the caller needs to
    // locate the whole field (label + control) for assertions.
    let childNode = children;
    if (testId && React.isValidElement(children)) {
        const existing = children.props && children.props["data-testid"];
        if (!existing) {
            childNode = React.cloneElement(children, { "data-testid": testId });
        }
    }
    return (
        <div
            className={`wiz-field${wide ? " wiz-field-wide" : ""}`}
            data-testid={testId ? `${testId}-wrap` : undefined}
        >
            <label className="mono">{label}</label>
            {childNode}
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Wizard shell                                                          */
/* -------------------------------------------------------------------- */
function deepSet(target, path, value) {
    const keys = path.split(".");
    const out = { ...target };
    let cur = out;
    for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        cur[k] = { ...(cur[k] || {}) };
        cur = cur[k];
    }
    cur[keys[keys.length - 1]] = value;
    return out;
}

function TeamApplyWizard({ app: initialApp, employer }) {
    const { t } = useTranslation();
    const [app, setApp] = useState(initialApp);
    const [advancing, setAdvancing] = useState(false);
    const [advErr, setAdvErr] = useState(null);

    const step = app.current_step || 1;

    // URL hash sync
    useEffect(() => {
        const onHash = () => {
            const m = /#step-(\d+)/.exec(window.location.hash);
            if (m) {
                const n = Number(m[1]);
                if (n >= 1 && n <= TOTAL_STEPS && n !== app.current_step) {
                    setApp((a) => ({ ...a, current_step: n }));
                }
            }
        };
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, [app.current_step]);

    useEffect(() => {
        if (typeof window !== "undefined") {
            const target = `#step-${step}`;
            if (window.location.hash !== target) {
                window.history.replaceState(null, "", target);
            }
        }
    }, [step]);

    // Per-step payload snapshot (what to send on autosave)
    const payload = useMemo(() => {
        const fields = {
            1: [],
            2: ["confirmed_company_info"],
            3: ["deduction_frequency"],
            4: ["location_model", "locations"],
            5: ["class_structure", "classes"],
            6: ["eligibility_window", "waiting_period_days"],
            7: ["rider_elections"],
            8: ["erisa_status", "erisa_notes"],
            9: ["hr_admin_contact"],
            10: ["communication_preferences"],
            11: [],
        }[step] || [];
        const out = {};
        for (const f of fields) out[f] = app[f];
        return out;
    }, [app, step]);

    const { status: saveStatus, savedAt } = useAutosave({
        step,
        payload,
        enabled: step !== 1 && step !== 11 && app.status === "in_progress",
        onSaved: (res) => {
            // merge masked EIN + server-side updates
            if (res?.application) {
                setApp((a) => ({
                    ...a,
                    ...res.application,
                    current_step: a.current_step, // keep local step (we drive it)
                }));
            }
            track("team_apply_step_save", { step });
        },
    });

    const setField = useCallback((path, value) => {
        setApp((a) => deepSet(a, path, value));
    }, []);

    // Unload warning
    useEffect(() => {
        const handler = (e) => {
            if (saveStatus === "saving" || saveStatus === "error") {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [saveStatus]);

    // Abandon tracking
    useEffect(() => {
        const handler = () => {
            if (app.status === "in_progress" && step > 1 && step < 11) {
                track("team_apply_abandon", { step, application_id: app.application_id });
            }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [app.status, step, app.application_id]);

    const goBack = async () => {
        if (step <= 1) return;
        try {
            const r = await backApplication();
            track("team_apply_step_back", { from: step, to: r.current_step });
            setApp((a) => ({ ...a, current_step: r.current_step }));
        } catch {
            /* ignore */
        }
    };
    const goNext = async () => {
        setAdvancing(true);
        setAdvErr(null);
        try {
            const r = await advanceApplication();
            track("team_apply_step_advance", { from: step, to: r.current_step });
            setApp((a) => ({
                ...a,
                current_step: r.current_step,
                status: r.current_step === 11 ? "proposal_pending" : a.status,
            }));
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && detail.missing_fields) {
                setAdvErr(detail.missing_fields);
            } else {
                setAdvErr([t("team.apply.wizard.advanceErrorGeneric")]);
            }
            track("team_apply_step_advance_fail", { step });
        } finally {
            setAdvancing(false);
        }
    };

    const jumpToStep = async (target) => {
        // Use /back repeatedly to keep server-side current_step in sync.
        // Only allow jumping backward; forward jumps go through /advance.
        if (target >= step) return;
        try {
            let cur = step;
            while (cur > target) {
                const r = await backApplication();
                cur = r.current_step;
            }
            setApp((a) => ({ ...a, current_step: cur }));
        } catch (_) { /* ignore */ }
    };

    const onSubmitted = (res) => {
        setApp((a) => ({
            ...a,
            status: res.status,
            submitted_at: res.submitted_at,
            current_step: res.current_step || 12,
        }));
    };

    const renderStep = () => {
        switch (step) {
            case 1: return <Screen1 app={app} employer={employer} />;
            case 2: return <Screen2 app={app} setField={setField} />;
            case 3: return <Screen3 app={app} setField={setField} />;
            case 4: return <Screen4 app={app} setField={setField} />;
            case 5: return <Screen5 app={app} setField={setField} />;
            case 6: return <Screen6 app={app} setField={setField} />;
            case 7: return <Screen7 app={app} setField={setField} />;
            case 8: return <Screen8 app={app} setField={setField} />;
            case 9: return <Screen9 app={app} setField={setField} employer={employer} />;
            case 10: return <Screen10 app={app} setField={setField} />;
            case 11: return <Screen11 app={app} onEdit={jumpToStep} onSubmitted={onSubmitted} />;
            case 12: return <Screen12 app={app} proposalId={employer?.proposal_id} />;
            default: return <Screen1 app={app} employer={employer} />;
        }
    };

    return (
        <section className="lg-section team-track wiz-track" data-testid="team-apply-page">
            <div className="wiz-header">
                <div>
                    <div className="section-label mono">{t("team.apply.wizard.header")}</div>
                    <h1 data-testid="wiz-h1">
                        <I18nText
                            i18nKey="team.apply.wizard.h1"
                            values={{ employerName: employer?.employer_name || "" }}
                        />
                    </h1>
                </div>
                <SaveIndicator status={saveStatus} savedAt={savedAt} />
            </div>

            <ProgressBar step={step} />

            {renderStep()}

            {advErr && (
                <div className="wiz-warn" data-testid="wiz-advance-err">
                    <strong>{t("team.apply.wizard.missingTitle")}</strong>
                    <ul>
                        {advErr.map((f) => <li key={f}><code>{f}</code></li>)}
                    </ul>
                </div>
            )}

            <div className="wiz-footer">
                <button
                    type="button"
                    className="link-inline"
                    onClick={goBack}
                    disabled={step <= 1 || step >= 12}
                    data-testid="wiz-back"
                >
                    ←︎ {t("team.apply.wizard.back")}
                </button>
                <div className="wiz-footer-right">
                    {step < 11 && (
                        <button
                            type="button"
                            className="btn-apply-submit"
                            onClick={goNext}
                            disabled={advancing}
                            data-testid="wiz-next"
                        >
                            {advancing
                                ? t("team.apply.wizard.saving")
                                : step === 10
                                  ? t("team.apply.wizard.submit")
                                  : t("team.apply.wizard.next")}
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
}

/* -------------------------------------------------------------------- */
/* Page entry                                                            */
/* -------------------------------------------------------------------- */
export default function TeamApply() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [employer, setEmployer] = useState(null);
    const [app, setApp] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        track("team_apply_view", {});
        document.title = t("team.apply.title");
        (async () => {
            try {
                const me = await employerMe();
                setEmployer(me);
                const started = await startApplication();
                setApp(started);
            } catch (e) {
                if (e?.response?.status === 401) {
                    navigate("/team/login?next=/team/apply");
                } else {
                    setErr(t("errors.generic"));
                }
            }
        })();
    }, [navigate, t]);

    // Phase 3 shell consolidation — wrap every return state (loading,
    // error, wizard) in <PortalChrome> so the authenticated employer
    // shell is always visible. Sign-out lives in PortalChrome; the old
    // in-wizard LOGOUT button is gone.
    if (err) {
        return (
            <PortalChrome
                actorType="employer"
                displayName={employer?.employer_name || ""}
            >
                <section className="lg-section team-track">
                    <div className="team-panel"><div className="apply-err">{err}</div></div>
                </section>
            </PortalChrome>
        );
    }
    if (!employer || !app) {
        return (
            <PortalChrome actorType="employer" displayName={employer?.employer_name || ""}>
                <section className="lg-section team-track" data-testid="team-apply-loading">
                    <div className="team-panel"><p className="team-sub">{t("team.apply.loading")}</p></div>
                </section>
            </PortalChrome>
        );
    }
    return (
        <PortalChrome
            actorType="employer"
            displayName={employer?.employer_name || ""}
        >
            <TeamApplyWizard
                app={app.application ? { ...app.application, current_step: app.current_step, application_id: app.application_id, status: app.status } : app}
                employer={employer}
            />
        </PortalChrome>
    );
}
